import { defineStore } from 'pinia'
import { ref } from 'vue'
import { io, type Socket } from 'socket.io-client'
import { api, socketOrigin } from '@/api/client'
import type { JobProgress } from '@/api/types-recipe'

/**
 * Background work reports progress over socket.io (rule 5). Polling is the
 * fallback, so a blocked websocket degrades to a slower experience rather
 * than a broken one.
 */
export const useJobsStore = defineStore('jobs', () => {
  const byId = ref<Record<string, JobProgress>>({})
  let socket: Socket | null = null

  function connect(accessToken: string) {
    if (socket) return
    // The API's origin where there is one, the page's otherwise. Under
    // Capacitor the page's origin is the APK, and a socket aimed there
    // retries against the asset loader forever.
    const url = socketOrigin()
    socket = url
      ? io(url, { path: '/socket.io', auth: { token: accessToken } })
      : io({ path: '/socket.io', auth: { token: accessToken } })
    socket.on('job:progress', (event: JobProgress) => {
      byId.value = { ...byId.value, [event.jobId]: event }
    })
  }

  function disconnect() {
    socket?.disconnect()
    socket = null
  }

  /** Resolve when the job finishes, whichever way. */
  async function waitFor(jobId: string, timeoutMs = 180_000): Promise<JobProgress> {
    const deadline = Date.now() + timeoutMs
    for (;;) {
      const live = byId.value[jobId]
      if (live && ['done', 'failed', 'cancelled'].includes(live.status)) return live

      const polled = await api.get<JobProgress>(`/jobs/${jobId}`)
      byId.value = { ...byId.value, [jobId]: polled }
      if (['done', 'failed', 'cancelled'].includes(polled.status)) return polled

      if (Date.now() > deadline) throw new Error('That job is taking too long')
      await new Promise((resolve) => setTimeout(resolve, 1200))
    }
  }

  async function cancel(jobId: string) {
    await api.post(`/jobs/${jobId}/cancel`)
  }

  return { byId, connect, disconnect, waitFor, cancel }
})
